import { KeyboardEvent, useEffect, useRef } from 'react';
import { buildClassName } from 'utils/build-class-name';
import useLocationSearch from 'hooks/use-location-search';
import useOutsideClickHandler from 'hooks/use-outside-click-handler';
import InputWithIcons from 'components/control/input-with-icons';
import SuggestedLocationDropdown from 'components/suggested-location-dropdown';
import SearchIcon from 'components/icon/search-icon-with-bg';
import CloseIcon from 'components/icon/close-icon';
import styles from './style.module.scss';
import { useFeaturesContext } from 'contexts';
import { mapPageIds } from 'constants/test-constants';
import { useThemeContext } from 'contexts/theme';
import { useSearchBarTracking } from 'hooks/use-search-bar-tracking';
import { SEARCH_OPTION_AGENTS, SEARCH_OPTION_BUY, SearchOptions } from '@tenants/types/theme';
import { isZoocasaTenant } from '@tenants';
import { GTM_CLICK_SEARCH_BAR_SUBMITTED } from 'constants/events';
import { trackEvent } from 'utils/google-tag-manager';

import type SearchPrediction from 'data/search-predictions';

interface Props {
  locationQuery: string;
  isLocationDropdownActive: boolean;
  isAreaSearch?: boolean;
  setLocationQuery: (query: string) => void;
  setIsLocationDropdownActive: (isActive: boolean) => void;
  setActivePrediction?: (searchPrediction: SearchPrediction) => void;
  onClick: (searchPrediction: SearchPrediction) => void;
  className?: string;
  chosenArea?: string;
  setChosenArea?: (area: string) => void;
  placeholder?: string;
  searchOption?: SearchOptions;
  onSearchIconClick?: (searchPredictions?: SearchPrediction[], query?: string) => void;
}

export default function LocationSearch({ locationQuery, isLocationDropdownActive, isAreaSearch, setLocationQuery, setIsLocationDropdownActive, setActivePrediction = (_: SearchPrediction) => undefined, onClick, className, chosenArea, setChosenArea, placeholder, searchOption = SEARCH_OPTION_BUY, onSearchIconClick }: Props) {
  const { tenantId } = useThemeContext(); // FIXME: tenant logic should be provided by parent component
  const isZoocasaTheme = isZoocasaTenant(tenantId);
  const { features } = useFeaturesContext();
  const { isLoadingSearchPredictions, searchPredictions, searchAgentPredictions, isTyping, waitForPredictionsToLoad } = useLocationSearch({
    queryString: locationQuery,
    useUsListings: features.useUsListings,
    searchOptions: searchOption,
  });
  const isAgentSearch = searchOption === SEARCH_OPTION_AGENTS;

  const { handleFocus, handleInput, handleBlur, markSearchAsSubmitted } = useSearchBarTracking({
    textInputRef: null,
    locationQuery,
    isHomePage: false,
    onSearchPage: false,
    isInPanel: false,
  });

  const searchPredictionsRef = useRef<SearchPrediction[]>([]);
  useEffect(() => {
    searchPredictionsRef.current = searchPredictions;
  }, [searchPredictions]);

  const handleSearchIconClick = async () => {
    await waitForPredictionsToLoad();

    const waitUntilPredictionsAvailable = async (timeoutMs = 2000) => {
      if (searchPredictionsRef.current.length > 0) return;
      await new Promise<void>(resolve => {
        const start = Date.now();
        const interval = setInterval(() => {
          const hasResults = searchPredictionsRef.current.length > 0;
          const timedOut = Date.now() - start >= timeoutMs;
          if (hasResults || timedOut) {
            clearInterval(interval);
            resolve();
          }
        }, 50);
      });
    };

    await waitUntilPredictionsAvailable();

    markSearchAsSubmitted();
    if (onSearchIconClick) {
      onSearchIconClick(searchPredictionsRef.current, locationQuery);
    }
    trackEvent(GTM_CLICK_SEARCH_BAR_SUBMITTED);
  };

  const handleSuggestedLocationClick = (searchPrediction: SearchPrediction) => {
    markSearchAsSubmitted();
    onClick(searchPrediction);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleSearchIconClick();
    }
  };

  return (
    <div className={buildClassName(styles.component, className)} ref={useOutsideClickHandler(() => { if (isAreaSearch) { setIsLocationDropdownActive(false); } })}>
      <SuggestedLocationDropdown
        isLoading={isLoadingSearchPredictions}
        searchPredictions={searchPredictions}
        setActivePrediction={setActivePrediction}
        onClick={handleSuggestedLocationClick}
        isActive={isLocationDropdownActive}
        isAreaSearch={isAreaSearch}
        isAdvancedSearch={true}
        locationQuery={locationQuery}
        isTyping={isTyping}
        hideSearchPanel={() => setIsLocationDropdownActive(false)}
        disablePopularSearches={isAgentSearch || isAreaSearch}
        searchAgentPredictions={searchAgentPredictions}
      />
      <InputWithIcons
        RightIcon={SearchIcon}
        MiddleIcon={CloseIcon}
        value={locationQuery}
        placeholder={placeholder}
        testId={mapPageIds.mapPageSearchbar}
        testIdMiddleIcon="mapPageSearchbarClear"
        padded={isAgentSearch}
        className={buildClassName(isZoocasaTheme && styles['zoocasa-theme'], styles['location-search'])}
        handleMiddleIconClick={() => {
          setLocationQuery('');
          if (setChosenArea) { setChosenArea(''); }
          handleInput('');
        }}
        handleRightIconClick={handleSearchIconClick}
        onValueChange={value => {
          setLocationQuery(value);
          handleInput(value);
        }}
        chosenArea={chosenArea}
        onClick={() => setIsLocationDropdownActive(true)}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
      />
    </div>
  );
}
